import React, { useState } from 'react';
import { Mail, Download, CheckCircle, BookOpen, FileText, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Newsletter Signup Component with Lead Magnets
 * Author: VB Entreprise
 */
const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [selectedLeadMagnet, setSelectedLeadMagnet] = useState('ebook');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const leadMagnets = [
    {
      id: 'ebook',
      title: 'Digital Transformation Guide',
      description: 'Complete guide to modernizing your business with digital solutions',
      icon: BookOpen,
      type: 'eBook',
      pages: '45 pages'
    },
    {
      id: 'report',
      title: '2024 Tech Trends Report',
      description: 'Comprehensive analysis of emerging technologies and their business impact',
      icon: FileText,
      type: 'Report',
      pages: '32 pages'
    },
    {
      id: 'checklist',
      title: 'Website Launch Checklist',
      description: 'Step-by-step checklist to ensure your website launch is successful',
      icon: ClipboardList,
      type: 'Checklist',
      pages: '15 items'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      try {
        const response = await fetch('/api/newsletter', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email, 
            leadMagnet: selectedLeadMagnet,
            selectedResource: leadMagnets.find(m => m.id === selectedLeadMagnet)?.title
          }),
        });

        if (response.ok) {
          setIsSubmitted(true);
          setEmail('');
        } else {
          throw new Error('Failed to subscribe');
        }
      } catch (error) {
        console.error('Error subscribing to newsletter:', error);
        alert('There was an error subscribing to the newsletter. Please try again.');
      }
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">
          Your download is ready. Check your email for the link to your free resource.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="text-jordy-blue-600 font-medium hover:text-jordy-blue-700 transition-colors"
        >
          Subscribe another email
        </button>
      </motion.div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-jordy-blue-50 to-white rounded-2xl p-8 border border-jordy-blue-200">
      <div className="text-center mb-8">
        <Mail className="w-12 h-12 text-jordy-blue-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Get Free Resources & Updates
        </h3>
        <p className="text-gray-600">
          Subscribe to our newsletter and get instant access to premium resources, industry insights, and exclusive content.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Lead Magnet Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Choose your free resource:
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {leadMagnets.map((magnet) => (
              <motion.button
                key={magnet.id}
                type="button"
                onClick={() => setSelectedLeadMagnet(magnet.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  selectedLeadMagnet === magnet.id
                    ? 'border-jordy-blue-500 bg-jordy-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start space-x-3">
                  <magnet.icon className={`w-6 h-6 flex-shrink-0 mt-1 ${
                    selectedLeadMagnet === magnet.id ? 'text-jordy-blue-600' : 'text-gray-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs font-medium text-gray-500">{magnet.type}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-400">{magnet.pages}</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      {magnet.title}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {magnet.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Email Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-jordy-blue-500"
              required
            />
            <Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          className="w-full bg-jordy-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors flex items-center justify-center space-x-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Download className="w-5 h-5" />
          <span>Get Free Resource</span>
        </motion.button>

        {/* Privacy Notice */}
        <p className="text-xs text-gray-500 text-center">
          By subscribing, you agree to receive our newsletter and marketing communications. 
          You can unsubscribe at any time. We respect your privacy and will never share your email.
        </p>
      </form>

      {/* Benefits */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-3">What you'll get:</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            'Weekly industry insights and trends',
            'Exclusive case studies and success stories',
            'Early access to new resources and tools',
            'Expert tips and best practices'
          ].map((benefit, index) => (
            <div key={index} className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-gray-600">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsletterSignup; 